export const AGENT_CHANNEL_KEYS = ["call", "sms", "whatsapp", "email"] as const;
export type AgentChannel = (typeof AGENT_CHANNEL_KEYS)[number];

export const resolveAgentChannels = (
  channels?: string[] | null
): AgentChannel[] => {
  const known = (channels || []).filter((channel): channel is AgentChannel =>
    (AGENT_CHANNEL_KEYS as readonly string[]).includes(channel)
  );
  return known.length ? known : ["call"];
};

export const agentUsesCallDirection = (
  channels?: string[] | null
): boolean => resolveAgentChannels(channels).includes("call");

/** Call agents keep inbound/outbound. Text, WhatsApp, and email are always inbound. */
export const resolveAgentDirection = (
  channels?: string[] | null,
  direction?: string | null
): "inbound" | "outbound" => {
  if (!agentUsesCallDirection(channels)) return "inbound";
  return direction === "inbound" ? "inbound" : "outbound";
};

/** Greeting is used for Call, Text, WhatsApp, and Email agents. */
export const agentNeedsGreeting = (
  channels?: string[] | null,
  _direction?: string | null
): boolean => {
  const resolved = resolveAgentChannels(channels);
  return (
    resolved.includes("call") ||
    resolved.includes("sms") ||
    resolved.includes("whatsapp") ||
    resolved.includes("email")
  );
};

export interface IAgent {
  org_id: string;
  _id: string;
  agent_name: string;
  agent_description: string;
  channels?: string[];
  direction: "outbound" | "inbound";
  model_framework: string;
  LLM: string;
  voice: string;
  language: string;
  tool?: number[];
  knowledge_base_ids: {
    _id: string;
    company_id: string;
    file_name: string;
    base_knowledge_file_url: string;
    presigned_url: string;
  }[];
  greeting_message: string;
  system_message: string;
  voicemail_message?: string;
  voice_configs?: {
    model_framework?: string;
    voice?: string;
    tts_provider?: string;
    stt_provider?: string;
    greeting_message?: string;
    voicemail_message?: string;
    ending_message?: string;
  };
  parameters?: {
    system_variables: { [key: string]: string };
    custom_variables: { [key: string]: string };
  };
  rag_configs?: {
    pinecone?: {
      embedding_model?: string;
      index_name?: string;
      index_host?: string;
    };
    openai?: {
      vector_ids?: any[];
    };
  };
  stats: {
    number_of_calls: number;
    average_duration: string;
    completion_rate: string;
    total_second: number;
  };
  createdAt: string;
}

export interface ICreateAgent {
  org_id: string;
  agent_name: string;
  agent_description: string;
  channels?: string[];
  direction: "outbound" | "inbound";
  model_framework?: string;
  LLM?: string;
  language?: string;
  tool?: number[];
  knowledge_base_ids?: string[];
  system_message: string;
  voice_configs: {
    model_framework?: string;
    voice?: string;
    tts_provider?: string;
    stt_provider?: string;
    greeting_message: string;
    voicemail_message?: string;
    ending_message?: string;
  };
  rag_configs?: {
    pinecone?: {
      embedding_model?: string;
      index_name?: string;
      index_host?: string;
    };
    openai?: {
      vector_ids?: any[];
    };
  };
  parameters?: {
    system_variables?: { [key: string]: string };
    custom_variables: { [key: string]: string };
  };
}

export interface IUpdateAgent {
  agent_name?: string;
  agent_description?: string;
  channels?: string[];
  direction?: "outbound" | "inbound";
  model_framework?: string;
  LLM?: string;
  language?: string;
  system_message?: string;
  voice_configs?: {
    model_framework?: string;
    voice?: string;
    tts_provider?: string;
    stt_provider?: string;
    greeting_message?: string;
    voicemail_message?: string;
    ending_message?: string;
  };
  tool?: number[];
  knowledge_base_ids?: string[];
  rag_configs?: {
    pinecone?: {
      embedding_model?: string;
      index_name?: string;
      index_host?: string;
    };
    openai?: {
      vector_ids?: any[];
    };
  };
  parameters?: {
    system_variables?: { [key: string]: string };
    custom_variables?: { [key: string]: string };
  };
}

export interface IGetAgents {
  limit: number;
  page: number;
  keyword: string;
  direction?: "outbound" | "inbound";
  channel?: string;
  sort?: Record<string, 1 | -1>;
}

export const AGENTS_VOICE = [
  {
    name: "alloy",
    value: "alloy",
  },
  {
    name: "ballad",
    value: "ballad",
  },
  {
    name: "coral",
    value: "Ashley",
  },
  {
    name: "sage",
    value: "gJx1vCzNCD1EQHT212Ls",
  },
  {
    name: "chole",
    value: "933563129e564b19a115bedd57b7406a",
  },
  {
    name: "edgar",
    value: "fQj4gJSexpu8RDE2Ii5m",
  }
  // {
  //   name: "ndivia",
  //   value: "English-US.Female-1",
  // },
];

export const TOOL_TYPE_GROUPS = [
  { value: "general", label: "General" },
  { value: "customize", label: "Customize" },
  { value: "internal", label: "Internal" },
];

export const TOOL_CHANNEL_KEYS = ["call", "sms", "whatsapp", "email"] as const;

export const DEFAULT_TOOL_CHANNELS: AgentChannel[] = [...AGENT_CHANNEL_KEYS];

export const TOOL_CHANNELS_BY_FUNCTION: Record<string, AgentChannel[]> = {
  createEvent: DEFAULT_TOOL_CHANNELS,
  kycVerification: DEFAULT_TOOL_CHANNELS,
  disputeInvestigate: DEFAULT_TOOL_CHANNELS,
  identifyCustomerInboundCallACN: DEFAULT_TOOL_CHANNELS,
  calculateDistance: DEFAULT_TOOL_CHANNELS,
  handleVectorSearch: DEFAULT_TOOL_CHANNELS,
  recordTimeSlots: DEFAULT_TOOL_CHANNELS,
  sendTextMessage: ["call", "email"],
  getCalendarEvents: DEFAULT_TOOL_CHANNELS,
  findCustomer: DEFAULT_TOOL_CHANNELS,
  endCall: ["call"],
  selectProvider: DEFAULT_TOOL_CHANNELS,
  getPromotion: DEFAULT_TOOL_CHANNELS,
  registerCustomer: DEFAULT_TOOL_CHANNELS,
  escalateToHuman: DEFAULT_TOOL_CHANNELS,
  sendDisputeEmail: DEFAULT_TOOL_CHANNELS,
  identifyTransaction: DEFAULT_TOOL_CHANNELS,
  getCurrencyRate: DEFAULT_TOOL_CHANNELS,
  checkWeeklyAvailability: DEFAULT_TOOL_CHANNELS,
  logCustomerInCall: DEFAULT_TOOL_CHANNELS,
  createCase: DEFAULT_TOOL_CHANNELS,
  sendTextMessageACN: ["call", "email"],
  transferCall: ["call"],
  convertCurrency: DEFAULT_TOOL_CHANNELS,
  transferToAfterHourLineNumberACN: ["call"],
  handleMediaMessage: DEFAULT_TOOL_CHANNELS,
};

export type ToolChannelRef = {
  function_name?: string;
  channels?: string[];
} | string | null | undefined;

export const channelsForFunction = (
  functionName?: string | null
): AgentChannel[] => {
  const mapped = TOOL_CHANNELS_BY_FUNCTION[String(functionName || "")];
  return mapped ? [...mapped] : [...DEFAULT_TOOL_CHANNELS];
};

export const normalizeToolChannels = (
  channels?: string[] | null,
  functionName?: string | null
): AgentChannel[] => {
  const list = (channels || []).filter((channel): channel is AgentChannel =>
    (AGENT_CHANNEL_KEYS as readonly string[]).includes(channel)
  );
  const unique = list.filter(
    (channel, index) => list.indexOf(channel) === index
  );
  return unique.length ? unique : channelsForFunction(functionName);
};

export const toolAppliesToChannel = (
  toolOrName: ToolChannelRef,
  channel?: string | null
): boolean => {
  if (!channel) return true;
  if (toolOrName && typeof toolOrName === "object") {
    return normalizeToolChannels(
      toolOrName.channels,
      toolOrName.function_name
    ).includes(channel as AgentChannel);
  }
  return channelsForFunction(String(toolOrName || "")).includes(
    channel as AgentChannel
  );
};

export const filterToolNamesForChannel = (
  toolNames: Array<string | number> | null | undefined,
  channel?: string | null,
  catalog?: Array<{ function_name?: string; channels?: string[] }>
): string[] => {
  const byName = new Map(
    (catalog || []).map((tool) => [String(tool.function_name || ""), tool])
  );
  const seen = new Set<string>();
  const next: string[] = [];
  for (const name of toolNames || []) {
    const functionName = String(name);
    if (!functionName || seen.has(functionName)) continue;
    const tool = byName.get(functionName) || functionName;
    if (!toolAppliesToChannel(tool, channel)) continue;
    seen.add(functionName);
    next.push(functionName);
  }
  return next;
};
